import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { QrCode } from "lucide-react";
import api from "../../lib/api";
import { submitRedsysForm } from "../../lib/redsysForm";
import { AceptacionCondicionesPago } from "../../components/legal/AceptacionCondicionesPago";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { formatFecha, formatFechaHora } from "../../lib/dates";

interface ModalidadPaquete {
  modalidadId: number;
  nombre: string;
  tipo: "diario" | "anual";
  precioVenta: number | null;
  precioBaseCc?: number | null;
  precioBase?: number;
}

interface SeguroCard {
  id: number;
  modalidad?: { nombre?: string; tipo?: string };
  modalidades_paquete?: ModalidadPaquete[];
  eventoReferencia?: string | null;
  fechaInicio?: string;
  fechaFin?: string;
  fechaInicioMin?: string | null;
  fechaFinMax?: string | null;
  numPilotos?: number;
  importeTotal?: number;
  estado?: "activo" | "cancelado" | string;
  pilotosPendientes?: number;
  metodoPago?: string;
  origen?: string;
}

interface SeguroDetail extends SeguroCard {
  modalidad?: { nombre: string; tipo: string };
  modalidades_paquete?: ModalidadPaquete[];
  /** Backend: activo, con pilotos pendientes y fechas válidas */
  puede_pagar?: boolean;
  pilotosPendientes?: number;
  pilotos: Array<{
    id: number;
    nif: string;
    nombre: string;
    apellidos: string;
    email: string;
    telefono: string;
    modalidadId?: number | null;
    fechaInicio?: string;
    fechaFin?: string;
    esFacturable?: boolean;
    bajaAt?: string | null;
    certificadoUrl?: string | null;
    estado?: "pendiente" | "pagado" | "cancelado" | "error_pago";
    errorPagoMotivo?: string | null;
    emitido?: boolean;
    emitidoAt?: string | null;
    paidAt?: string | null;
  }>;
}

function formatModalidadesPaquete(
  modalidadesPaquete: ModalidadPaquete[] | undefined,
  fallbackNombre?: string
): string {
  if (modalidadesPaquete && modalidadesPaquete.length > 0) {
    return modalidadesPaquete
      .map((m) => `${m.nombre} (${m.tipo === "anual" ? "A" : "D"})`)
      .join(", ");
  }
  return fallbackNombre ?? "—";
}

function formatEuro(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(2)} €`;
}

function TablaPreciosModalidades({
  modalidadesPaquete,
}: {
  modalidadesPaquete: ModalidadPaquete[] | undefined;
}) {
  if (!modalidadesPaquete?.length) return null;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Modalidad</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Tipo</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">Precio piloto</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">Precio CC</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">Tu margen</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {modalidadesPaquete.map((m) => {
            const cc = m.precioBaseCc ?? m.precioBase ?? null;
            const margen =
              m.precioVenta != null && cc != null ? m.precioVenta - cc : null;
            return (
              <tr key={m.modalidadId}>
                <td className="px-4 py-2 text-[#2C2C2C]">{m.nombre}</td>
                <td className="px-4 py-2 text-gray-600">
                  {m.tipo === "anual" ? "Anual" : "Diario"}
                </td>
                <td className="px-4 py-2 text-right text-[#2C2C2C]">
                  {m.precioVenta != null ? formatEuro(m.precioVenta) : "—"}
                </td>
                <td className="px-4 py-2 text-right text-[#2C2C2C]">{formatEuro(cc)}</td>
                <td
                  className={`px-4 py-2 text-right font-medium ${
                    margen != null && margen > 0
                      ? "text-green-600"
                      : "text-gray-500"
                  }`}
                >
                  {margen != null ? formatEuro(margen) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function estadoBadgeSeguro(estado: string | undefined) {
  if (estado === "activo") return <Badge variant="success">Activo</Badge>;
  if (estado === "cancelado") return <Badge variant="danger">Cancelado</Badge>;
  return <Badge variant="neutral">{estado ?? "—"}</Badge>;
}

function estadoCeldaTabla(s: SeguroCard) {
  const pendientes = s.pilotosPendientes ?? 0;
  if (s.estado === "activo") {
    return (
      <div>
        <Badge variant="success">Activo</Badge>
        {pendientes > 0 && (
          <p className="mt-1 text-xs text-gray-500">
            {pendientes} piloto{pendientes === 1 ? "" : "s"} pendiente{pendientes === 1 ? "" : "s"}
          </p>
        )}
      </div>
    );
  }
  if (s.estado === "cancelado") return <Badge variant="danger">Cancelado</Badge>;
  return <Badge variant="neutral">{s.estado ?? "—"}</Badge>;
}

export function MisSegurosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [list, setList] = useState<SeguroCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [estadoFilter, setEstadoFilter] = useState("activo");
  const [confirmDeletePaqueteId, setConfirmDeletePaqueteId] = useState<number | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [detail, setDetail] = useState<SeguroDetail | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (estadoFilter !== "todos") params.estado = estadoFilter;
      const res = await api.get<SeguroCard[]>("/cliente/seguros", { params });
      setList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [estadoFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openDetail = useCallback(async (id: number) => {
    setModalOpen(true);
    setDetail(null);
    setPayError(null);
    try {
      const res = await api.get<SeguroDetail>(`/cliente/seguros/${id}`);
      setDetail(res.data);
    } catch {
      setDetail(null);
    }
  }, []);

  const lastConsumedOpenKey = useRef<string | null>(null);
  useEffect(() => {
    const st = location.state as { openSeguroId?: number; tab?: "resumen" | "pilotos"; seguroId?: number } | null | undefined;
    const id = st?.openSeguroId ?? st?.seguroId;
    if (id == null) {
      lastConsumedOpenKey.current = null;
      return;
    }
    if (st?.tab === "pilotos") {
      navigate("/dashboard/pilotos", { replace: true, state: { seguroId: id } });
      return;
    }
    const key = `${id}:resumen`;
    if (lastConsumedOpenKey.current === key) return;
    lastConsumedOpenKey.current = key;
    void openDetail(id);
    navigate(".", { replace: true, state: {} });
  }, [location.state, navigate, openDetail]);

  const [aceptaCondicionesPago, setAceptaCondicionesPago] = useState(false);

  useEffect(() => {
    setAceptaCondicionesPago(false);
  }, [detail?.id, modalOpen]);

  const handlePagarAhora = async () => {
    if (!detail?.id) return;
    if (!aceptaCondicionesPago) {
      setPayError("Debes aceptar las condiciones de compra para continuar con el pago.");
      return;
    }
    setPayError(null);
    setPayLoading(true);
    try {
      const res = await api.post<{ formData: { Ds_SignatureVersion: string; Ds_MerchantParameters: string; Ds_Signature: string; actionUrl: string } }>(`/cliente/pagos/iniciar/${detail.id}`);
      if (res.data.formData?.actionUrl) {
        submitRedsysForm(res.data.formData);
      }
    } catch (err: unknown) {
      setPayError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Error al iniciar el pago. Inténtalo de nuevo."
      );
    } finally {
      setPayLoading(false);
    }
  };

  const isQrGenericoLegacy = (s: { origen?: string; numPilotos?: number }) =>
    s.origen === "qr" && (s.numPilotos ?? 0) === 1;

  const handleEliminarPaquete = async () => {
    if (confirmDeletePaqueteId == null) return;
    const id = confirmDeletePaqueteId;
    try {
      const res = await api.delete<{ cancelado?: boolean; eliminado?: boolean }>(
        `/cliente/seguros/${id}`
      );
      setConfirmDeletePaqueteId(null);
      if (detail?.id === id) {
        setModalOpen(false);
        setDetail(null);
      }
      fetchList();
      if (res.data.cancelado) {
        setSuccessMsg("Paquete cancelado.");
      } else {
        setSuccessMsg("Paquete eliminado.");
      }
    } catch {
      setConfirmDeletePaqueteId(null);
    }
  };

  return (
    <div className="space-y-6">
      {successMsg && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {successMsg}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-[#2C2C2C]">Mis Seguros</h1>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
  <select
    value={estadoFilter}
    onChange={(e) => {
      setEstadoFilter(e.target.value);
      setSuccessMsg(null);
    }}
    className="w-full rounded border px-3 py-2 text-sm sm:w-auto"
  >
    <option value="todos">Todos</option>
    <option value="activo">Activos</option>
    <option value="cancelado">Cancelados</option>
  </select>
  <Button
    className="w-full sm:w-auto"
    onClick={() => navigate("/dashboard/nuevo-seguro")}
  >
    Crear paquete de seguros
  </Button>
</div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Paquete</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Modalidad</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Vigencia</th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Pilotos</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Importe</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? (
              [1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 rounded bg-gray-200 animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                  No hay seguros con este filtro.
                </td>
              </tr>
            ) : (
              list.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-[#2C2C2C]">
                    {s.origen === "qr" && (s.numPilotos ?? 0) === 1 ? (
                      <span className="inline-flex items-center gap-1.5">
                        <QrCode className="h-4 w-4 shrink-0 text-[#1A3AB8]" aria-hidden />
                        Registro QR
                      </span>
                    ) : (
                      s.eventoReferencia?.trim() ? s.eventoReferencia : "Sin paquete"
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#2C2C2C]">
                    {formatModalidadesPaquete(s.modalidades_paquete)}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#2C2C2C]">
                    {s.fechaInicioMin && s.fechaFinMax ? (
                      <>
                        <div>{formatFecha(s.fechaInicioMin)}</div>
                        <div className="text-gray-500">→ {formatFecha(s.fechaFinMax)}</div>
                      </>
                    ) : (
                      <span className="text-gray-500">Sin pilotos</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-[#2C2C2C]">{s.numPilotos ?? 0}</td>
                  <td className="px-4 py-3 text-right text-sm text-[#2C2C2C]">{Number(s.importeTotal ?? 0).toFixed(2)} €</td>
                  <td className="px-4 py-3 text-sm">{estadoCeldaTabla(s)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button size="sm" variant="secondary" onClick={() => openDetail(s.id)}>
                        Ver detalle
                      </Button>
                      {s.origen !== "qr" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => navigate(`/dashboard/seguros/${s.id}/editar`)}
                        >
                          Editar paquete
                        </Button>
                      )}
                      {s.estado === "activo" && s.origen !== "qr" && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => setConfirmDeletePaqueteId(s.id)}
                        >
                          Eliminar
                        </Button>
                      )}
                      {s.estado === "activo" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            navigate("/dashboard/pilotos", { state: { seguroId: s.id } })
                          }
                        >
                          Gestionar pilotos
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setDetail(null); }} title="Detalle del seguro" footer={null}>
        {!detail ? (
          <div className="py-8 text-center text-gray-500">Cargando…</div>
        ) : (() => {
              const pilotosFact = detail.pilotos?.filter((p) => p.esFacturable) ?? [];
              const pilotosPendientes = pilotosFact.filter((p) => p.estado === "pendiente").length;
              const fechaMin = pilotosFact.reduce(
                (min, p) => (!min || (p.fechaInicio && p.fechaInicio < min) ? p.fechaInicio ?? min : min),
                "" as string
              );
              const fechaMax = pilotosFact.reduce(
                (max, p) => (!max || (p.fechaFin && p.fechaFin > max) ? p.fechaFin ?? max : max),
                "" as string
              );
              const vigenciaLabel =
                pilotosFact.length === 0
                  ? "Sin pilotos asignados"
                  : fechaMin && fechaMax
                    ? `${formatFechaHora(fechaMin)} → ${formatFechaHora(fechaMax)}`
                    : "Sin pilotos asignados";
              const pilotosLink = (
                <Link
                  to="/dashboard/pilotos"
                  state={{ seguroId: detail.id }}
                  className="font-medium text-[#1A3AB8] hover:underline"
                >
                  Mis pilotos
                </Link>
              );
              return (
              <div className="space-y-4">
                <dl className="grid gap-2 text-sm">
                  <dt className="font-medium text-gray-500">Modalidad</dt>
                  <dd>{formatModalidadesPaquete(detail.modalidades_paquete)}</dd>
                  <dt className="font-medium text-gray-500">Paquete</dt><dd>{detail.eventoReferencia?.trim() || "Sin paquete"}</dd>
                  <dt className="font-medium text-gray-500">Vigencia cobertura</dt><dd>{vigenciaLabel}</dd>
                  <dt className="font-medium text-gray-500">Pilotos / Importe</dt><dd>{detail.numPilotos ?? 0} · {Number(detail.importeTotal ?? 0).toFixed(2)} €</dd>
                  <dt className="font-medium text-gray-500">Estado</dt><dd>{estadoBadgeSeguro(detail.estado)}</dd>
                </dl>
                <TablaPreciosModalidades modalidadesPaquete={detail.modalidades_paquete} />
                {pilotosPendientes > 0 && (
                  <p className="text-sm text-amber-700">
                    {pilotosPendientes} piloto{pilotosPendientes === 1 ? "" : "s"} pendiente{pilotosPendientes === 1 ? "" : "s"} de validación — gestiona en {pilotosLink}.
                  </p>
                )}
                {detail.estado === "activo" && pilotosPendientes > 0 && detail.metodoPago === "tpv" && isQrGenericoLegacy(detail) && (
                  <>
                  <AceptacionCondicionesPago
                    checked={aceptaCondicionesPago}
                    onChange={setAceptaCondicionesPago}
                    id="acepta-condiciones-mis-seguros"
                  />
                  <Button
                    loading={payLoading}
                    onClick={handlePagarAhora}
                    disabled={
                      (detail.numPilotos ?? 0) === 0 ||
                      detail.puede_pagar === false ||
                      !aceptaCondicionesPago
                    }
                    title={
                      (detail.numPilotos ?? 0) === 0
                        ? "Añade al menos un piloto antes de asegurar"
                        : detail.puede_pagar === false
                          ? "La fecha de inicio de la cobertura ya ha pasado"
                          : ""
                    }
                  >
                    Asegurar
                  </Button>
                  </>
                )}
                {detail.estado === "activo" && pilotosPendientes > 0 && detail.metodoPago === "transferencia" && isQrGenericoLegacy(detail) && (detail.numPilotos ?? 0) >= 1 && (
                  <p className="text-sm text-gray-600">
                    Valida el piloto en {pilotosLink} para activar la cobertura.
                  </p>
                )}
                {detail.estado === "activo" && pilotosPendientes > 0 && detail.puede_pagar === false && (detail.numPilotos ?? 0) >= 1 && (
                  <p className="text-sm text-amber-700">
                    No se puede asegurar: la fecha de inicio de la cobertura ya ha pasado. Crea un nuevo paquete con otra fecha.
                  </p>
                )}
                {detail.estado === "activo" && (detail.numPilotos ?? 0) === 0 && (detail.metodoPago === "tpv" || detail.metodoPago === "transferencia") && (
                  <p className="text-sm text-gray-500">
                    Añade pilotos en {pilotosLink} para poder asegurar.
                  </p>
                )}
                {payLoading && <p className="text-sm text-gray-500">Redirigiendo al banco…</p>}
                {payError && (
                  <div className="mt-2 rounded bg-red-50 p-2 text-sm text-red-600">{payError}</div>
                )}
              </div>
              );
            })()}
      </Modal>

      <ConfirmDialog
        isOpen={confirmDeletePaqueteId !== null}
        onClose={() => setConfirmDeletePaqueteId(null)}
        onConfirm={handleEliminarPaquete}
        title="Eliminar paquete"
        message="¿Seguro que quieres eliminar este paquete? Si tiene pilotos o recibos se cancelará. Si está vacío se eliminará permanentemente."
        confirmLabel="Eliminar"
        variant="danger"
      />
    </div>
  );
}
